import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, RefreshCw, Printer, CheckCircle, XCircle, Send, Loader2, X, Trash2, Building2, Settings } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import {
  getBillingProfile, updateBillingProfile, getPlatformInvoices, getInvoiceTiers,
  generateInvoice, setInvoiceStatus, deletePlatformInvoice, getBillingSummary
} from '../../config/api';

const money = (v) => '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const date = (v) => (v ? new Date(v).toLocaleDateString() : '—');
const STATUS_PILL = {
  DRAFT: 'bg-gray-100 text-gray-700', ISSUED: 'bg-blue-100 text-blue-700',
  PAID: 'bg-green-100 text-green-700', VOID: 'bg-red-100 text-red-700'
};
const TIER_LABEL = { TIER_1: 'Tier 1 — Under $10M', TIER_2: 'Tier 2 — $10M–$100M', TIER_3: 'Tier 3 — $100M–$500M+' };

export default function PlatformInvoices() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole ? hasRole(['ADMIN']) : false;

  const [profile, setProfile] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({ total: 0, byStatus: {} });
  const [billing, setBilling] = useState(null); // plan + usage + charges snapshot
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showGen, setShowGen] = useState(false);
  const [pForm, setPForm] = useState({ companyName: '', billingTier: '', commercialNdcCount: 0, erpIntegration: false, billingEmail: '' });
  const [gen, setGen] = useState({ invoiceType: 'COMBINED', tier: '', ndcCount: '', erpIntegration: false, includeUsage: false, period: 'last-12-months' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prof, inv, tr, sum] = await Promise.all([getBillingProfile(), getPlatformInvoices(), getInvoiceTiers(), getBillingSummary().catch(() => null)]);
      setProfile(prof);
      setInvoices(inv.invoices || []);
      setSummary(inv.summary || { total: 0, byStatus: {} });
      setTiers(tr || {});
      setBilling(sum);
    } catch (e) { toast.error('Failed to load billing'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openProfile = () => { setPForm({ companyName: profile?.companyName || '', billingTier: profile?.billingTier || '', commercialNdcCount: profile?.commercialNdcCount ?? 0, erpIntegration: !!profile?.erpIntegration, billingEmail: profile?.billingEmail || '' }); setShowProfile(true); };
  const saveProfile = async () => {
    setBusy(true);
    try { const p = await updateBillingProfile({ ...pForm, commercialNdcCount: Number(pForm.commercialNdcCount) || 0 }); setProfile(p); toast.success('Billing profile saved'); setShowProfile(false); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to save'); }
    finally { setBusy(false); }
  };

  const openGen = () => {
    if (!profile?.billingTier) return toast.error('Set the company billing tier first (Billing profile)');
    setGen({ invoiceType: 'COMBINED', tier: profile.billingTier, ndcCount: profile.commercialNdcCount ?? 0, erpIntegration: !!profile.erpIntegration, includeUsage: true, period: 'last-12-months' });
    setShowGen(true);
  };
  const submitGen = async () => {
    setBusy(true);
    try { await generateInvoice({ ...gen, ndcCount: Number(gen.ndcCount) || 0 }); toast.success('Invoice generated'); setShowGen(false); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to generate'); }
    finally { setBusy(false); }
  };

  const act = async (id, action) => {
    try { await setInvoiceStatus(id, action); toast.success(`Invoice ${action === 'pay' ? 'marked paid' : action + 'd'}`); setDetail(null); load(); }
    catch (e) { toast.error(e.response?.data?.error || `Failed to ${action}`); }
  };
  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoiceNumber}? This cannot be undone.`)) return;
    try { await deletePlatformInvoice(inv.id); toast.success('Invoice deleted'); setDetail(null); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to delete'); }
  };

  const printInvoice = (inv) => {
    const rows = (inv.lineItems || []).map((l) => `<tr><td>${l.description}</td><td style="text-align:right">${money(l.amount)}</td></tr>`).join('');
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>${inv.invoiceNumber}</title><style>body{font-family:system-ui,sans-serif;padding:40px;color:#1e293b}h1{color:#0f2a4a}table{width:100%;border-collapse:collapse;margin-top:20px}td,th{padding:10px;border-bottom:1px solid #e2e8f0;text-align:left}tfoot td{font-weight:700;border-top:2px solid #0f2a4a;border-bottom:none}</style></head><body>
      <div style="display:flex;justify-content:space-between"><div><h1>Invoice</h1><div>${inv.invoiceNumber}</div></div>
      <div style="text-align:right"><strong>PharmaChargeback</strong><br/>Status: ${inv.status}<br/>Issued: ${date(inv.issueDate)}<br/>Due: ${date(inv.dueDate)}</div></div>
      <p><strong>Bill to:</strong> ${inv.customerName}</p>
      <table><thead><tr><th>Description</th><th style="text-align:right">Amount</th></tr></thead><tbody>${rows}</tbody>
      <tfoot><tr><td>Subtotal</td><td style="text-align:right">${money(inv.subtotal)}</td></tr>
      <tr><td>Tax</td><td style="text-align:right">${money(inv.taxAmount)}</td></tr>
      <tr><td>Total</td><td style="text-align:right">${money(inv.total)}</td></tr></tfoot></table></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  const tierOptions = Object.entries(tiers).map(([k, v]) => ({ value: k, label: v.label, base: v.base }));

  return (
    <div className="space-y-5">
      {/* Company billing card */}
      <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary-50 p-2.5"><Building2 className="h-6 w-6 text-primary-700" /></div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400">Billed company</p>
            <p className="text-lg font-bold text-gray-900">{profile?.companyName || <span className="italic text-gray-400">Not set</span>}</p>
            <p className="text-xs text-gray-500">
              {profile?.billingTier ? TIER_LABEL[profile.billingTier] : 'No tier set'}
              {profile?.commercialNdcCount ? ` · ${profile.commercialNdcCount} NDCs` : ''}
              {profile?.erpIntegration ? ' · ERP' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} title="Refresh" className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></button>
          {isAdmin && <button onClick={openProfile} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"><Settings className="h-4 w-4" /> Billing profile</button>}
          {isAdmin && <button onClick={openGen} className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700"><Plus className="h-4 w-4" /> Generate invoice</button>}
        </div>
      </div>

      {!isAdmin && <p className="text-xs text-gray-400">Platform invoices are issued by the platform owner. You can view your plan, usage, and print what your company owes.</p>}

      {/* Plan & Usage — read-only snapshot of subscription, allowance usage, and accrual */}
      {billing && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Current plan + pricing */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Current plan</p>
            {billing.tier ? (
              <>
                <p className="mt-1 text-base font-bold text-gray-900">{TIER_LABEL[billing.tier.key] || billing.tier.label}</p>
                <dl className="mt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><dt className="text-gray-500">Annual subscription</dt><dd className="font-semibold text-gray-900">{money(billing.tier.base)}/yr</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Included claims</dt><dd className="font-medium text-gray-900">{Number(billing.tier.includedClaims || 0).toLocaleString()}/yr</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Overage rate</dt><dd className="font-medium text-gray-900">${Number(billing.tier.overageRate || 0).toFixed(2)}/claim</dd></div>
                </dl>
              </>
            ) : (
              <p className="mt-2 text-sm italic text-gray-400">No subscription tier set{isAdmin ? ' — configure the billing profile.' : '.'}</p>
            )}
          </div>

          {/* Usage vs allowance (trailing 12 months) */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Claims ingested — last 12 months</p>
            {billing.usage ? (
              <>
                <p className="mt-1 text-base font-bold text-gray-900">
                  {Number(billing.usage.claimsProcessed).toLocaleString()}
                  <span className="text-sm font-normal text-gray-500"> / {Number(billing.usage.includedClaims).toLocaleString()} included</span>
                </p>
                {(() => {
                  const pct = billing.usage.percentUsed;
                  const used = Number(billing.usage.claimsProcessed) || 0;
                  const over = billing.usage.over > 0;
                  // Show a visible sliver + "<1%" when there's real-but-tiny usage that
                  // would otherwise round to 0% and read as "nothing used".
                  const width = pct == null ? 0 : Math.min(100, pct > 0 ? pct : (used > 0 ? 1 : 0));
                  const pctText = pct == null ? '—' : (pct === 0 && used > 0 ? '<1%' : `${pct}%`);
                  const barColor = over ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-accent-500';
                  return (
                    <>
                      <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
                        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${width}%` }} />
                      </div>
                      <p className={`mt-1.5 text-xs font-medium ${over ? 'text-red-600' : 'text-gray-500'}`}>
                        {pctText === '—' ? '—' : `${pctText} of allowance used`}
                        {over ? ` · ${Number(billing.usage.over).toLocaleString()} over` : ''}
                      </p>
                    </>
                  );
                })()}
                <p className="mt-2 text-[11px] text-gray-400">Counts claims uploaded into the system. Validating or adjudicating existing claims doesn't change this.</p>
              </>
            ) : (
              <p className="mt-2 text-sm italic text-gray-400">Set a tier to track usage.</p>
            )}
          </div>

          {/* Charged so far + accrual */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Charged to date</p>
            <p className="mt-1 text-base font-bold text-primary-700">{money(billing.charges?.invoicedTotal)}<span className="text-sm font-normal text-gray-500"> invoiced</span></p>
            <dl className="mt-3 space-y-1.5 text-sm">
              <div className="flex justify-between"><dt className="text-gray-500">Paid</dt><dd className="font-medium text-green-700">{money(billing.charges?.paidTotal)}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500">Outstanding</dt><dd className="font-medium text-amber-700">{money(billing.charges?.outstandingTotal)}</dd></div>
              {billing.usage && billing.usage.over > 0 && (
                <div className="flex justify-between border-t border-gray-100 pt-1.5"><dt className="text-gray-500">Overage accruing</dt><dd className="font-semibold text-red-600">{money(billing.usage.overageAmount)}</dd></div>
              )}
            </dl>
          </div>
        </div>
      )}

      {/* Compact counts */}
      <div className="flex gap-3">
        <div className="rounded-lg bg-white p-3 shadow-sm ring-1 ring-gray-100"><span className="text-xs text-gray-500">Invoices</span><p className="text-xl font-bold text-gray-900">{invoices.length}</p></div>
        <div className="rounded-lg bg-white p-3 shadow-sm ring-1 ring-gray-100"><span className="text-xs text-gray-500">Total billed</span><p className="text-xl font-bold text-primary-700">{money(summary.total)}</p></div>
        <div className="rounded-lg bg-white p-3 shadow-sm ring-1 ring-gray-100"><span className="text-xs text-gray-500">Paid</span><p className="text-xl font-bold text-green-700">{summary.byStatus?.PAID || 0}</p></div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left">Invoice</th><th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Issued</th><th className="px-4 py-3 text-left">Due</th>
              <th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
            ) : invoices.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">No invoices yet.{isAdmin ? ' Use “Generate invoice”.' : ''}</td></tr>
            ) : invoices.map((inv) => (
              <tr key={inv.id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 font-mono text-xs text-gray-700">{inv.invoiceNumber}</td>
                <td className="px-4 py-2.5 text-gray-600">{inv.invoiceType}</td>
                <td className="px-4 py-2.5 text-gray-600">{date(inv.issueDate)}</td>
                <td className="px-4 py-2.5 text-gray-600">{date(inv.dueDate)}</td>
                <td className="px-4 py-2.5 text-right font-medium text-gray-900">{money(inv.total)}</td>
                <td className="px-4 py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_PILL[inv.status] || 'bg-gray-100'}`}>{inv.status}</span></td>
                <td className="px-4 py-2.5 text-right"><button onClick={() => setDetail(inv)} className="text-primary-600 hover:underline text-xs font-medium">View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Billing profile modal */}
      {showProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between"><h3 className="text-lg font-semibold text-gray-900">Company billing profile</h3><button onClick={() => setShowProfile(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button></div>
            <div className="space-y-3">
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Company name</label><input value={pForm.companyName} onChange={(e) => setPForm((f) => ({ ...f, companyName: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Subscription tier</label>
                <select value={pForm.billingTier} onChange={(e) => setPForm((f) => ({ ...f, billingTier: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Not set</option>
                  {tierOptions.map((t) => <option key={t.value} value={t.value}>{t.label} — ${t.base.toLocaleString()}/yr</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-gray-600 mb-1"># Commercial NDCs</label><input type="number" min="0" max="50" value={pForm.commercialNdcCount} onChange={(e) => setPForm((f) => ({ ...f, commercialNdcCount: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" /></div>
                <label className="flex items-end gap-2 pb-2 text-sm text-gray-700"><input type="checkbox" checked={pForm.erpIntegration} onChange={(e) => setPForm((f) => ({ ...f, erpIntegration: e.target.checked }))} className="h-4 w-4" /> ERP integration</label>
              </div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Billing email (optional)</label><input value={pForm.billingEmail} onChange={(e) => setPForm((f) => ({ ...f, billingEmail: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" /></div>
            </div>
            <div className="mt-5 flex justify-end gap-2"><button onClick={() => setShowProfile(false)} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={saveProfile} disabled={busy} className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save</button></div>
          </div>
        </div>
      )}

      {/* Generate modal */}
      {showGen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-1 flex items-center justify-between"><h3 className="text-lg font-semibold text-gray-900">Generate invoice</h3><button onClick={() => setShowGen(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button></div>
            <p className="mb-4 text-sm text-gray-500">Bill to <strong>{profile?.companyName}</strong></p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Tier</label>
                  <select value={gen.tier} onChange={(e) => setGen((g) => ({ ...g, tier: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                    {tierOptions.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Invoice type</label>
                  <select value={gen.invoiceType} onChange={(e) => setGen((g) => ({ ...g, invoiceType: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                    <option value="COMBINED">Combined (subscription + setup)</option>
                    <option value="SUBSCRIPTION">Subscription only (annual)</option>
                    <option value="IMPLEMENTATION">Implementation only (one-time)</option>
                    <option value="USAGE">Usage overage only</option>
                  </select>
                </div>
              </div>
              {gen.invoiceType !== 'IMPLEMENTATION' && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input type="checkbox" checked={gen.includeUsage || gen.invoiceType === 'USAGE'} disabled={gen.invoiceType === 'USAGE'}
                      onChange={(e) => setGen((g) => ({ ...g, includeUsage: e.target.checked }))} className="h-4 w-4" />
                    Bill claim overage
                  </label>
                  {tiers[gen.tier] && (
                    <p className="mt-1 text-xs text-gray-500">
                      {TIER_LABEL[gen.tier]} includes <strong>{(tiers[gen.tier].includedClaims || 0).toLocaleString()}</strong> claims/yr; extra at <strong>${Number(tiers[gen.tier].overageRate || 0).toFixed(2)}</strong>/claim.
                    </p>
                  )}
                  {(gen.includeUsage || gen.invoiceType === 'USAGE') && (
                    <div className="mt-2"><label className="block text-xs font-medium text-gray-600 mb-1">Usage period</label>
                      <select value={gen.period} onChange={(e) => setGen((g) => ({ ...g, period: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                        <option value="last-12-months">Last 12 months</option>
                        <option value="ytd">Year to date</option>
                        <option value="this-quarter">This quarter</option>
                        <option value="last-month">Last month</option>
                        <option value="this-month">This month</option>
                        <option value="last-year">Last calendar year</option>
                      </select>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-gray-600 mb-1"># Commercial NDCs</label><input type="number" min="0" max="50" value={gen.ndcCount} onChange={(e) => setGen((g) => ({ ...g, ndcCount: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" /></div>
                <label className="flex items-end gap-2 pb-2 text-sm text-gray-700"><input type="checkbox" checked={gen.erpIntegration} onChange={(e) => setGen((g) => ({ ...g, erpIntegration: e.target.checked }))} className="h-4 w-4" /> ERP integration</label>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2"><button onClick={() => setShowGen(false)} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={submitGen} disabled={busy} className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Generate</button></div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-1 flex items-start justify-between">
              <div><h3 className="text-lg font-semibold text-gray-900">{detail.invoiceNumber}</h3><p className="text-sm text-gray-500">{detail.customerName} · <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_PILL[detail.status]}`}>{detail.status}</span></p></div>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-3 divide-y divide-gray-100 border-y border-gray-100">
              {(detail.lineItems || []).map((l, i) => (<div key={i} className="flex items-center justify-between py-2 text-sm"><span className="text-gray-700">{l.description}</span><span className="font-medium text-gray-900">{money(l.amount)}</span></div>))}
            </div>
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{money(detail.subtotal)}</span></div>
              <div className="flex justify-between text-gray-500"><span>Tax</span><span>{money(detail.taxAmount)}</span></div>
              <div className="flex justify-between text-base font-bold text-gray-900"><span>Total</span><span>{money(detail.total)}</span></div>
            </div>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button onClick={() => printInvoice(detail)} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"><Printer className="h-4 w-4" /> Print</button>
              {isAdmin && detail.status === 'DRAFT' && <button onClick={() => act(detail.id, 'issue')} className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"><Send className="h-4 w-4" /> Issue</button>}
              {isAdmin && detail.status === 'ISSUED' && <button onClick={() => act(detail.id, 'pay')} className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"><CheckCircle className="h-4 w-4" /> Mark paid</button>}
              {isAdmin && (detail.status === 'DRAFT' || detail.status === 'ISSUED') && <button onClick={() => act(detail.id, 'void')} className="inline-flex items-center gap-1.5 rounded-md border border-red-300 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"><XCircle className="h-4 w-4" /> Void</button>}
              {isAdmin && <button onClick={() => remove(detail)} title="Delete invoice" className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"><Trash2 className="h-4 w-4" /> Delete</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
